import { formIndexesInRouteOrder } from "@src/shipments/features/stopReordering";

type FormStop = { id?: string };
type ExistingStop = { id?: string; stopSequenceNumber?: number | null };

/**
 * Returns 1-based sequence numbers parallel to formStops when an edit
 * both adds a new stop AND the order already carries sequence numbers.
 * Returns null when no re-sequencing should happen — the caller then
 * omits the sequence field from its payload so the backend preserves.
 *
 * Existing stops keep their saved route order, not the form's order, so adding a stop never changes
 * their route order. A new stop goes just ahead of the existing stop that follows it in the form,
 * or at the end; if any existing stop has no route number, the form order is used.
 */
export function resequenceStopsOnEdit(
  formStops: FormStop[],
  existingStops: ExistingStop[]
): number[] | null {
  const hasNewStop = formStops.some((s) => !s.id);
  const anyExistingHasSeq = existingStops.some(
    (s) => s.stopSequenceNumber != null
  );
  if (!hasNewStop || !anyExistingHasSeq) return null;

  const routeNumbersById = new Map<string, number>();
  existingStops.forEach((stop) => {
    if (stop.id && stop.stopSequenceNumber != null) {
      routeNumbersById.set(stop.id, stop.stopSequenceNumber);
    }
  });
  const everyFormStopHasRouteNumber = formStops.every(
    (stop) => !stop.id || routeNumbersById.has(stop.id)
  );
  if (!everyFormStopHasRouteNumber) return formStops.map((_, i) => i + 1);

  const routeOrder = formIndexesInRouteOrder(formStops, routeNumbersById);
  const numbers: number[] = new Array(formStops.length);
  routeOrder.forEach((formIndex, rank) => {
    numbers[formIndex] = rank + 1;
  });
  return numbers;
}
